<template>
    <b-modal :id="modalId" size="lg" :title="'Tarefa: ' + (taskInfo.reason || taskInfo.protocol)" scrollable centered
        no-stacking>
        <b-container fluid>
            <div class="task-details">
                <div>
                    <div class="task-info">
                        <div class="info-grid">
                            <div class="info-item" v-if="taskInfo.protocol">
                                <div class="info-label">Código da Tarefa:</div>
                                <div class="info-value">{{ taskInfo.protocol }}</div>
                            </div>

                            <div class="info-item" v-if="taskInfo.ref_protocol">
                                <div class="info-label">Protocolo do Atendimento:</div>
                                <div class="info-value">{{ taskInfo.ref_protocol }}</div>
                            </div>

                            <div class="info-item" v-if="taskInfo.service_date">
                                <div class="info-label">Data do Malote:</div>
                                <div class="info-value">{{ formatDate(taskInfo.service_date) }}</div>
                            </div>

                            <div class="info-item" v-if="taskInfo.service_type">
                                <div class="info-label">Tipo de Serviço:</div>
                                <div class="info-value">{{ taskInfo.service_type }}</div>
                            </div>

                            <div class="info-item" v-if="taskInfo.channel">
                                <div class="info-label">Canal:</div>
                                <div class="info-value">{{ formatChannel(taskInfo.channel) }}</div>
                            </div>

                            <div class="info-item" v-if="taskInfo.client_name">
                                <div class="info-label">Cliente:</div>
                                <div class="info-value">{{ taskInfo.client_name }}</div>
                            </div>

                            <div class="info-item" v-if="taskInfo.address">
                                <div class="info-label">Endereço:</div>
                                <div class="info-value">{{ taskInfo.address }}</div>
                            </div>

                            <div class="info-item" v-if="taskInfo.reason">
                                <div class="info-label">Motivo de acionamento:</div>
                                <div class="info-value">
                                    <span :class="{ 'itau-badge': taskInfo.is_itau }">{{ taskInfo.reason }}{{
                                        taskInfo.is_itau ? ' (Itaú)' : '' }}</span>
                                </div>
                            </div>

                            <div class="info-item" v-if="taskInfo.janela">
                                <div class="info-label">Janela:</div>
                                <div class="info-value">{{ taskInfo.janela }}</div>
                            </div>

                            <div class="info-item">
                                <div class="info-label">Base (Grupo):</div>
                                <div class="info-value">{{ taskInfo.owner_group?.name ?? "" }}</div>
                            </div>

                            <div class="info-item">
                                <div class="info-label">Responsável:</div>
                                <select v-model="selectedFields.user" class="input-edit-mode" v-if="editMode">
                                    <option value="">Selecione um responsável</option>
                                    <option v-for="user in filteredUsers" :key="user._id" :value="user._id">
                                        {{ user.name }}
                                    </option>
                                </select>
                                <div v-else class="info-value">{{ taskInfo.owner_user?.name ?? "" }}</div>
                            </div>

                            <div class="info-item">
                                <div class="info-label">Status:</div>
                                <div class="info-value">
                                    <select v-if="editMode" class="input-edit-mode" v-model="selectedStatus">
                                        <option v-for="status in statuses" :key="status._id" :value="status._id">
                                            {{ camelCaseString(status.name) }}
                                        </option>
                                    </select>
                                    <span v-else class="highlight-badge" :style="dinamicStyle(taskInfo.status?.color)">
                                        {{ camelCaseString(taskInfo.status?.name) }}
                                    </span>
                                </div>
                            </div>

                            <div class="info-item">
                                <div class="info-label">Prioridade:</div>
                                <div class="info-value">
                                    <span class="highlight-badge" :style="dinamicStyle(taskInfo.priority?.color)">
                                        {{ camelCaseString(taskInfo.priority?.name) }}
                                    </span>
                                </div>
                            </div>

                            <div class="info-item">
                                <div class="info-label">Cliente Itaú:</div>
                                <div class="info-value">
                                    <span v-if="taskInfo.is_itau" class="itau-badge">Sim</span>
                                    <span v-else>Não</span>
                                </div>
                            </div>

                            <div class="info-item" v-if="taskInfo.sla">
                                <div class="info-label">SLA:</div>
                                <div class="info-value" v-html="handleTicketSLA(taskInfo, slaTick)"></div>
                            </div>

                            <div class="info-item">
                                <div class="info-label">Criado em:</div>
                                <div class="info-value">{{ formatDate(taskInfo.createdAt) }}</div>
                            </div>

                            <div class="info-item">
                                <div class="info-label">Atualizado em:</div>
                                <div class="info-value">{{ formatDate(taskInfo.updatedAt) }}</div>
                            </div>

                            <div class="info-item" v-if="taskInfo.closed_at">
                                <div class="info-label">Concluído em:</div>
                                <div class="info-value">{{ formatDate(taskInfo.closed_at) }}</div>
                            </div>

                            <div class="info-item" v-if="taskInfo.closed_by">
                                <div class="info-label">Concluído por:</div>
                                <div class="info-value">{{ taskInfo.closed_by?.name ?? taskInfo.closed_by }}</div>
                            </div>
                        </div>

                        <div class="info-item mt-3" v-if="taskInfo.description || editMode">
                            <div class="info-label">Descrição:</div>
                            <textarea v-if="editMode" class="comment-input" v-model="taskInfo.description"></textarea>
                            <div class="info-value" v-else style="white-space: pre-wrap;">{{ taskInfo.description }}
                            </div>
                        </div>

                        <div class="info-item mt-3" v-if="taskInfo.attachments && taskInfo.attachments.length > 0">
                            <div class="info-label">Anexos:</div>
                            <div class="composer-attachments">
                                <ul>
                                    <li v-for="(attachment, index) in taskInfo.attachments" :key="index">
                                        <span>
                                            <i :class="getFileIcon(attachment.filename)"></i>
                                            <span>{{ attachment.filename }}</span>
                                            <small class="attachment-size">({{ formatFileSize(attachment.size)
                                            }})</small>
                                        </span>
                                        <button @click="downloadFile(generateDownloadURL(attachment.filename), attachment.filename)"
                                            class="download-attachment-btn" title="Download anexo">
                                            <i class="fas fa-download"></i>
                                        </button>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    <div class="comment-form comment-form--closing" style="margin-bottom: 10px;" v-if="editMode && closingComment.visible">
                        <div class="info-label closing-label">
                            <i class="fas fa-exclamation-circle"></i> Comentário de finalização (obrigatório)
                        </div>
                        <textarea class="comment-input comment-input--closing" placeholder="Adicione um comentário para finalizar a tarefa"
                            v-model="closingComment.text"></textarea>
                    </div>

                    <div class="btns-edit">
                        <div class="left" v-show="taskInfo?.prev_owner && taskInfo?.prev_owner?.group">
                            <button class="btn btn-success" v-show="editMode" @click="forwardBack">
                                <i class="fas fa-undo"></i> Salvar e devolver tarefa
                            </button>
                        </div>

                        <div class="right">
                            <button class="btn btn-secondary" v-show="!editMode" @click="editMode = true">
                                <i class="fas fa-edit"></i> Editar
                            </button>

                            <button class="btn btn-primary" v-show="editMode" @click="updateTask()">
                                <i class="far fa-save"></i> Salvar alterações
                            </button>

                            <button class="btn btn-danger" v-show="editMode" @click="cancelEdit()">
                                <i class="far fa-times-circle"></i> Cancelar
                            </button>
                        </div>
                    </div>

                    <div class="comment-section">
                        <div class="timeline">
                            <div class="timeline-header">
                                <h4>
                                    <i class="fas fa-comments"></i> Comentários
                                    <span class="badge badge-danger">{{ taskInfo.comments ? taskInfo.comments.length : 0
                                    }}</span>
                                </h4>
                                <button class="btn btn-primary" @click="showComments = !showComments">
                                    <span v-show="!showComments"><i class="fas fa-eye"></i> Exibir</span>
                                    <span v-show="showComments"><i class="fas fa-eye-slash"></i> Ocultar</span>
                                </button>
                            </div>

                            <div class="timeline-item" v-if="taskInfo.comments && showComments"
                                v-for="comment in taskInfo.comments" :key="comment._id">
                                <div class="timeline-icon">{{ comment._id % 2 == 1 ? "💬" : "🗨️" }}</div>
                                <div class="timeline-content">
                                    <div class="timeline-title">{{ comment.created_by ? comment.created_by.name : "" }}
                                    </div>
                                    <div class="timeline-time">{{ formatDate(comment.createdAt) }}</div>
                                    <div class="timeline-description">{{ comment.text }}</div>
                                    <div v-if="comment.attachments && comment.attachments.length > 0"
                                        class="composer-attachments mt-2">
                                        <ul>
                                            <li v-for="(attachment, aIdx) in comment.attachments" :key="aIdx">
                                                <span>
                                                    <i :class="getFileIcon(attachment.filename)"></i>
                                                    <span>{{ attachment.filename }}</span>
                                                    <small class="attachment-size">({{ formatFileSize(attachment.size)
                                                    }})</small>
                                                </span>
                                                <button @click="downloadFile(generateCommentDownloadURL(comment._id, attachment.filename), attachment.filename)"
                                                    class="download-attachment-btn" title="Download anexo">
                                                    <i class="fas fa-download"></i>
                                                </button>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>

                            <div :class="[showComments ? 'writter-comment-area' : '']">
                                <h5><i class="fas fa-pen-nib"></i> Escrever um novo comentário:</h5>
                                <div class="comment-form">
                                    <textarea class="comment-input" placeholder="Adicione um comentário interno..."
                                        v-model="newComment"></textarea>
                                    <div class="comment-attach-area">
                                        <div class="task-attach-btn" @click="addCommentAttach">
                                            <i class="fas fa-paperclip"></i> Anexar arquivos
                                        </div>
                                        <div v-if="commentAttachments.length > 0" class="task-attach-list">
                                            <div v-for="(file, index) in commentAttachments" :key="index"
                                                class="task-attach-item">
                                                <span class="task-attach-info">
                                                    <i :class="getFileIcon(file.name)"></i>
                                                    {{ file.name }}
                                                    <small class="text-muted">({{ formatFileSize(file.size) }})</small>
                                                </span>
                                                <button type="button" class="task-attach-remove"
                                                    @click="removeCommentAttach(index)" title="Remover">✕</button>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="comment-actions">
                                        <button class="btn btn-primary" @click="saveComment()">
                                            <i class="fas fa-save"></i> Adicionar Comentário
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="timeline mt-3">
                        <div class="timeline-header">
                            <h4>
                                <i class="far fa-clock"></i> Linha do Tempo
                                <span class="badge badge-danger">{{ taskInfo.timeline ? taskInfo.timeline.length : 0
                                }}</span>
                            </h4>
                            <button class="btn btn-primary" @click="showTimeline = !showTimeline">
                                <span v-show="!showTimeline"><i class="fas fa-eye"></i> Exibir</span>
                                <span v-show="showTimeline"><i class="fas fa-eye-slash"></i> Ocultar</span>
                            </button>
                        </div>

                        <div class="timeline-item" v-if="taskInfo.timeline && showTimeline"
                            v-for="event in taskInfo.timeline" :key="event._id">
                            <div class="timeline-icon">{{ selectTimelineIcon(event.event) }}</div>
                            <div class="timeline-content">
                                <div class="timeline-title">{{ event.event }}</div>
                                <div class="timeline-time">{{ formatDate(event.createdAt) }}</div>
                                <div class="timeline-description">{{ event.detail }}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </b-container>

        <template #modal-footer>
            <div class="w-100 d-flex justify-content-end">
                <TaskPDF :task="taskInfo"></TaskPDF>
            </div>
        </template>
    </b-modal>
</template>

<script>
import TaskPDF from './TaskPDF.vue';
import {
    dinamicStyle,
    formatDate,
    camelCaseString,
    selectTimelineIcon,
    handleTicketSLA,
} from '../../helpers/utils';

import { taskManagerAxios, TASKMANAGER_URL } from './connections/taskApiConnection.js';

export default {
    name: "TaskDetailsModal",
    components: { TaskPDF },
    props: {
        modalId: {
            type: String,
            default: 'bv-modal-task'
        },
        prioritiesList: {
            type: Array,
            default: () => []
        },
        usersList: {
            type: Array,
            default: () => []
        },
        reasonsList: {
            type: Array,
            default: () => []
        },
    },
    data: () => ({
        slaTick: 0,
        slaInterval: null,
        showComments: false,
        showTimeline: false,
        editMode: false,
        closingComment: {
            visible: false,
            text: ''
        },
        selectedStatus: null,
        selectedFields: {
            user: null,
            group: null,
            priority: null,
        },
        originalTaskData: {},
        statuses: [],
        newComment: null,
        commentAttachments: [],
        allGroups: [],
        allUsers: [],
        myUser: null,
        altPrevOwner: null,
        taskInfo: {
            _id: null,
            description: '',
            client_name: '',
            address: '',
            protocol: '',
            ref_protocol: '',
            channel: '',
            sla: null,
            reason: null,
            owner_user: null,
            owner_group: null,
            priority: null,
            status: null,
            comments: [],
            timeline: [],
            createdAt: '',
            updatedAt: '',
            closed_at: null,
            closed_by: null,
            prev_owner: null,
            is_itau: false,
            service_date: null,
            service_type: null,
            janela: null,
        },
    }),
    created() {
        if (user.getIdPrefix() && user.getName()) {
            this.myUser = {
                _id: user.getIdPrefix(),
                name: user.getName()
            }
        }
        this.getStatuses();
        this.getGroups();
        this.getUsers();
    },
    mounted() {
        this.slaInterval = setInterval(() => { this.slaTick++; }, 60000);
    },
    beforeDestroy() {
        if (this.slaInterval) clearInterval(this.slaInterval);
    },
    computed: {
        filteredUsers() {
            if (!this.selectedFields.group) return [];
            const group = this.allGroups.find(g => g._id === this.selectedFields.group);
            if (!group?.members?.length) return [];
            const filtered = this.allUsers.filter(u => group.members.some(m => (typeof m === 'object' ? m._id : m) == u._id));
            if (this.selectedFields.user && !filtered.some(u => u._id == this.selectedFields.user)) {
                const current = this.allUsers.find(u => u._id == this.selectedFields.user);
                if (current) return [current, ...filtered];
            }
            return filtered;
        },
    },
    watch: {
        selectedStatus(value) {
            try {
                const foundStatus = this.statuses.find(item => item._id == value);
                this.closingComment.visible = !!(foundStatus && foundStatus.is_finisher);
            } catch (error) {
                console.error(error);
            }
        },
        'selectedFields.group'() {
            if (this.editMode) this.selectedFields.user = null;
        },
    },
    methods: {
        dinamicStyle,
        formatDate,
        camelCaseString,
        selectTimelineIcon,
        handleTicketSLA,
        openModal(task) {
            this.editMode = false;
            this.showComments = false;
            this.showTimeline = false;
            this.newComment = null;
            this.commentAttachments = [];
            this.closingComment = { visible: false, text: '' };
            this.selectedFields.group = task.owner_group?._id ?? null;
            this.selectedFields.user = task.owner_user?._id ?? null;
            this.selectedFields.priority = task.priority?._id ?? null;
            this.selectedStatus = task.status?._id ?? null;
            this.originalTaskData = {
                description: task.description,
                client_name: task.client_name,
                address: task.address,
                protocol: task.protocol,
                ref_protocol: task.ref_protocol,
                channel: task.channel,
                reason: task.reason,
                is_itau: task.is_itau,
                service_date: task.service_date ? task.service_date.slice(0, 16) : null,
                service_type: task.service_type ?? null,
                janela: task.janela ?? null,
            };
            this.taskInfo = { ...task, comments: task.comments ?? [], timeline: task.timeline ?? [] };
            this.taskInfo.service_date = task.service_date ? task.service_date.slice(0, 16) : null;
            this.$bvModal.show(this.modalId);
            this.readNotification();

        },
        closeModal() {
            this.closingComment = { visible: false, text: '' };
            this.editMode = false;
            this.$bvModal.hide(this.modalId);
        },
        getGroups() {
            taskManagerAxios.get('/groups').then(({ data }) => {
                if (data) this.allGroups = data.map(item => ({
                    _id: item._id,
                    name: item.name,
                    members: item.members,
                }));
            }).catch(error => console.error(error));
        },
        getUsers() {
            taskManagerAxios.get("/users").then(({ data }) => {
                if (data) this.allUsers = data.map(item => ({
                    _id: item._id,
                    name: item.name,
                }));
            }).catch(error => console.error(error));
        },
        getStatuses() {
            taskManagerAxios.get('/statuses').then(({ data }) => {
                if (data) this.statuses = data.map(item => ({
                    _id: item._id,
                    name: item.name,
                    color: item.color,
                    is_finisher: item.is_finisher,
                }));
            }).catch(error => console.error(error));
        },
        handleForm() {
            try {
                if (this.closingComment.visible && !this.closingComment.text) {
                    Swal.fire({
                        type: 'error',
                        text: 'É obrigatório inserir um comentário para encerrar a tarefa!',
                    });
                    return false;
                }

                const foundStatus = this.statuses.find(item => item._id == this.selectedStatus);
                if (foundStatus) this.taskInfo.status = foundStatus;

                const foundGroup = this.allGroups.find(item => item._id == this.selectedFields.group);
                if (foundGroup) this.taskInfo.owner_group = foundGroup;
                else if (!this.selectedFields.group) this.taskInfo.owner_group = null;

                const foundUser = this.allUsers.find(item => item._id == this.selectedFields.user);
                if (foundUser) this.taskInfo.owner_user = foundUser;
                else if (!this.selectedFields.user) this.taskInfo.owner_user = null;

                const foundPriority = this.prioritiesList.find(item => item._id == this.selectedFields.priority);
                if (foundPriority) this.taskInfo.priority = foundPriority;

                return {
                    description: this.taskInfo.description || null,
                    client_name: this.taskInfo.client_name || null,
                    address: this.taskInfo.address || null,
                    protocol: this.taskInfo.protocol || null,
                    ref_protocol: this.taskInfo.ref_protocol || null,
                    channel: this.taskInfo.channel || null,
                    reason: this.taskInfo.reason || null,
                    is_itau: this.taskInfo.is_itau ?? false,
                    service_date: this.taskInfo.service_date || null,
                    service_type: this.taskInfo.service_type || null,
                    janela: this.taskInfo.reason === 'Previsão de Atendimento' ? (this.taskInfo.janela || null) : null,
                    owner_user: this.taskInfo.owner_user,
                    owner_group: this.taskInfo.owner_group,
                    priority: this.taskInfo.priority,
                    status: this.taskInfo.status,
                    comment: this.closingComment.text || null,
                    user: this.myUser,
                    prev_owner: this.altPrevOwner ?? this.taskInfo.prev_owner
                };
            } catch (error) {
                console.error(error);
                Swal.fire({
                    type: 'error',
                    text: 'Falha ao atualizar tarefa, por favor, valide todos os campos!',
                });
                return false;
            }
        },
        updateTask() {
            const form = this.handleForm();
            if (!form) return;

            taskManagerAxios.put(`/tasks/${this.taskInfo._id}`, form)
                .then(({ data }) => {
                    this.taskInfo.owner_user = data.owner_user;
                    this.taskInfo.owner_group = data.owner_group;
                    this.taskInfo.priority = data.priority;
                    this.taskInfo.status = data.status;
                    this.taskInfo.is_itau = data.is_itau;
                    this.taskInfo.service_date = data.service_date ? data.service_date.slice(0, 10) : null;
                    this.taskInfo.service_type = data.service_type ?? null;
                    this.taskInfo.janela = data.janela ?? null;
                    this.taskInfo.updatedAt = data.updatedAt;
                    if (data.timeline && data.timeline.length > 0) {
                        this.taskInfo.timeline.push(...data.timeline);
                    }
                    this.editMode = false;
                    this.closingComment = { visible: false, text: '' };
                    Swal.fire({
                        type: 'success',
                        text: 'Tarefa salva com sucesso',
                        timer: 1000,
                    }).then(() => {
                        if (data) {
                            this.$emit("updateTaskTable", data);
                            if (this.hasCloseModal(data)) this.closeModal();
                        }
                    });
                }).catch(error => {
                    console.error(error);
                    Swal.fire({
                        type: 'error',
                        text: error.response?.data?.message ?? 'Falha ao atualizar tarefa, por favor, valide todos os campos!',
                    });
                });
        },
        async saveComment() {
            if (!this.newComment) return;

            const attachments = await Promise.all(this.commentAttachments.map(f => this.fileToBase64(f)));

            taskManagerAxios.post(`/tasks/${this.taskInfo._id}/comments`, {
                text: this.newComment,
                user: this.myUser,
                attachments,
            }).then(({ data }) => {
                Swal.fire({ type: 'success', text: 'Comentário salvo com sucesso', timer: 1000 });
                if (!this.taskInfo.comments) this.taskInfo.comments = [];
                this.taskInfo.comments.push(data);
                this.newComment = null;
                this.commentAttachments = [];
            }).catch(error => {
                console.error(error);
                Swal.fire({ type: 'error', text: 'Falha ao salvar comentário!' });
            });
        },
        hasCloseModal(task) {
            if (task.closed_at) return true;
            const userId = this.myUser?._id ?? null;
            const isOwner = task.owner_user?._id == userId;
            const isGroupMember = task.owner_group?.members?.some(m => (typeof m === 'object' ? m._id : m) == userId);
            if (!isOwner && !isGroupMember) return true;
            return false;
        },
        fileToBase64(file) {
            return new Promise((resolve) => {
                const reader = new FileReader();
                reader.onload = (e) => resolve({
                    filename: file.name,
                    content: e.target.result.split(',')[1],
                });
                reader.readAsDataURL(file);
            });
        },
        addCommentAttach() {
            const input = document.createElement('input');
            input.type = 'file';
            input.multiple = true;
            input.onchange = (e) => {
                this.commentAttachments = [...this.commentAttachments, ...Array.from(e.target.files)];
            };
            input.click();
        },
        removeCommentAttach(index) {
            this.commentAttachments.splice(index, 1);
        },
        generateCommentDownloadURL(commentId, filename) {
            if (!commentId || !filename) return '';
            return `${TASKMANAGER_URL}/api/tasks/comments/${commentId}/attachments/${encodeURIComponent(filename)}`;
        },
        generateDownloadURL(filename) {
            if (!filename || !this.taskInfo._id) return '';
            return `${TASKMANAGER_URL}/api/tasks/${this.taskInfo._id}/attachments/${encodeURIComponent(filename)}`;
        },
        async downloadFile(url, filename) {
            if (!url || !filename) return;
            try {
                const response = await fetch(url, {
                    headers: { 'x-api-key': window.taskManagerApiKey ?? '' },
                });
                if (!response.ok) throw new Error(`Erro ao baixar arquivo: ${response.status}`);
                const blob = await response.blob();
                const blobUrl = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = blobUrl;
                a.download = filename;
                a.click();
                URL.revokeObjectURL(blobUrl);
            } catch (error) {
                console.error('[TaskDetailsModal] downloadFile:', error);
                Swal.fire({ type: 'error', text: 'Falha ao baixar o arquivo.' });
            }
        },
        getFileIcon(fileName) {
            if (!fileName) return 'fas fa-file-alt';
            const ext = fileName.split('.').pop().toLowerCase();
            const map = {
                jpg: 'fas fa-file-image', jpeg: 'fas fa-file-image', png: 'fas fa-file-image',
                gif: 'fas fa-file-image', bmp: 'fas fa-file-image', svg: 'fas fa-file-image',
                pdf: 'fas fa-file-pdf',
                doc: 'fas fa-file-word', docx: 'fas fa-file-word',
                xls: 'fas fa-file-excel', xlsx: 'fas fa-file-excel',
                ppt: 'fas fa-file-powerpoint', pptx: 'fas fa-file-powerpoint',
                txt: 'fas fa-file-alt',
                csv: 'fas fa-file-csv',
                mp3: 'fas fa-file-audio', wav: 'fas fa-file-audio',
                mp4: 'fas fa-file-video', avi: 'fas fa-file-video', mov: 'fas fa-file-video',
                zip: 'fas fa-file-archive', rar: 'fas fa-file-archive',
            };
            return map[ext] ?? 'fas fa-file-alt';
        },
        formatFileSize(bytes) {
            if (!bytes || bytes === 0) return '0 Bytes';
            const k = 1024;
            const sizes = ['Bytes', 'KB', 'MB', 'GB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
        },
        cancelEdit() {
            this.selectedFields.group = this.taskInfo.owner_group?._id ?? null;
            this.selectedFields.user = this.taskInfo.owner_user?._id ?? null;
            this.selectedFields.priority = this.taskInfo.priority?._id ?? null;
            this.selectedStatus = this.taskInfo.status?._id ?? null;
            this.taskInfo.description = this.originalTaskData.description;
            this.taskInfo.client_name = this.originalTaskData.client_name;
            this.taskInfo.address = this.originalTaskData.address;
            this.taskInfo.protocol = this.originalTaskData.protocol;
            this.taskInfo.ref_protocol = this.originalTaskData.ref_protocol;
            this.taskInfo.channel = this.originalTaskData.channel;
            this.taskInfo.reason = this.originalTaskData.reason;
            this.taskInfo.is_itau = this.originalTaskData.is_itau;
            this.taskInfo.service_date = this.originalTaskData.service_date;
            this.taskInfo.service_type = this.originalTaskData.service_type;
            this.taskInfo.janela = this.originalTaskData.janela;
            this.closingComment = { visible: false, text: '' };
            this.editMode = false;
        },
        formatChannel(channel) {
            if (channel === 'whatsapp') return 'Whatsapp';
            if (channel === 'telefone') return 'Telefone';
            if (channel === 'phone') return 'Telefone';
            if (channel === 'email') return 'E-mail';
            return channel;
        },
        readNotification() {
            taskManagerAxios.post(`/tasks/readNotification/${this.taskInfo._id}`)
                .then(() => {
                    this.$emit('notificationRead', this.taskInfo._id);
                }).catch(error => {
                    console.error(error);
                });
        },
        forwardBack() {
            if (!this.taskInfo?.prev_owner) return;
            if (!this.taskInfo?.prev_owner?.group) return;
            const prevUser = this.taskInfo?.prev_owner?.user ?? null;
            const prevGroup = this.taskInfo.prev_owner.group;
            let htmlInfo = `
                <div style="text-align: left;">
                    <p style="margin-bottom: 8px; margin-left: 5px;">Essa tarefa será devolvida para:</p>
                    <div style="background: #f8f9fa; border-radius: 4px; padding: 10px 14px;">
                        ${prevUser ? `<div style="margin-bottom: 6px;"><strong>Usuário:</strong> ${prevUser?.name}</div>` : ''}
                        <div><strong>Grupo:</strong> ${prevGroup?.name}</div>
                    </div>
                    <p style="margin-top: 12px; margin-left: 5px;">Deseja continuar?</p>
                </div>
            `;
            Swal.fire({
                title: 'Deseja devolver essa tarefa?',
                html: htmlInfo,
                type: 'question',
                showCancelButton: true,
                confirmButtonText: 'Encaminhar',
                cancelButtonText: 'Cancelar'
            }).then((result) => {
                if (result.value) {
                    const userPrevOwner = this.taskInfo.prev_owner?.user?._id ?? null;
                    if (prevUser) this.selectedFields.user = prevUser._id;
                    this.selectedFields.group = prevGroup._id;
                    this.altPrevOwner = {
                        group: this.taskInfo.owner_group,
                        user: this.taskInfo?.owner_user ?? this.myUser
                    }
                    this.updateTask();
                }
            });
        }
    },
}
</script>

<style scoped>
.task-details {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.task-info {
    background: #f8f9fa;
    padding: 15px;
    border-radius: 6px !important;
    margin-bottom: 5px;
}

.info-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 15px;
}

.info-item {
    display: flex;
    flex-direction: column;
    gap: 3px;
}

.info-label {
    font-size: 10px;
    color: #666;
    font-weight: bold;
    text-transform: uppercase;
}

.info-value {
    font-size: 12px;
    color: #333;
    font-weight: 500;
}

.itau-badge {
    background-color: #EC7000;
    color: #ffffff;
    font-weight: bold;
    padding: 2px 8px;
    border-radius: 4px;
}

.itau-select {
    border: 2px solid #EC7000 !important;
    background-color: #fff8f4 !important;
}

.input-edit-mode {
    height: 30px;
    width: 100%;
    font-size: 14px;
    border: 1px solid #c2c2c2;
    border-radius: 5px !important;
    padding: 2px 5px;
}

.highlight-badge {
    padding: 5px 10px;
    border-radius: 5px !important;
    font-size: 12px;
    font-weight: bold;
}

.btns-edit {
    margin-bottom: 10px;
    display: flex;
    justify-content: space-between;
    gap: 8px;
}

.comment-section {
    margin-top: 20px;
    padding-top: 15px;
    border-top: 1px solid #eee;
}

.timeline {
    background: white;
    border: 1px solid #ddd;
    border-radius: 6px !important;
    padding: 15px;
}

.timeline-header {
    display: flex;
    justify-content: space-between;
    font-weight: bold;
    margin-bottom: 15px;
    font-size: 14px;
    color: #333;
    border-bottom: 1px solid #eee;
    padding-bottom: 8px;
}

.timeline-item {
    display: flex;
    gap: 12px;
    margin-bottom: 15px;
    position: relative;
}

.timeline-item:not(:last-child)::after {
    content: '';
    position: absolute;
    left: 10px;
    top: 21px;
    width: 2px;
    height: calc(100% + 3px);
    background: #e0e0e0;
}

.timeline-icon {
    width: 20px;
    height: 20px;
    background: #ffffff;
    border-radius: 50% !important;
    border: 1px solid #c2c2c2;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    flex-shrink: 0;
    box-shadow: 1px 1px 1px black;
}

.timeline-content {
    flex: 1;
}

.timeline-title {
    font-weight: bold;
    font-size: 12px;
    margin-bottom: 2px;
}

.timeline-time {
    font-size: 10px;
    color: #666;
    margin-bottom: 5px;
}

.timeline-description {
    font-size: 11px;
    color: #333;
    line-height: 1.4;
}

.writter-comment-area {
    margin-top: 40px;
    border-top: 1px solid #c2c2c2;
    padding-top: 10px;
}

.comment-form {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.comment-input {
    width: 100%;
    min-height: 80px;
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 4px !important;
    font-size: 12px;
    resize: vertical;
}

.comment-form--closing {
    border: 1px solid #e65100;
    border-radius: 6px;
    padding: 10px 12px;
}

.closing-label {
    color: #e65100 !important;
    font-size: 11px !important;
}

.comment-input--closing {
    border-color: #e65100 !important;
}

.comment-actions {
    display: flex;
    gap: 10px;
}

.btn {
    padding: 8px 15px;
    border: 1px solid;
    border-radius: 4px !important;
    cursor: pointer;
    font-size: 11px;
    font-weight: bold;
    transition: all 0.3s;
}

.btn-primary {
    background-color: #2e8b8b;
    color: white;
    border-color: #2e8b8b;
}

.btn-primary:hover {
    background-color: #246969;
}

.btn-secondary {
    background-color: #6c757d;
    color: white;
    border-color: #6c757d;
}

.btn-danger {
    background-color: #dc3545;
    color: white;
    border-color: #dc3545;
}

.comment-attach-area {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin: 6px 0;
}

.task-attach-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 12px;
    width: 20%;
    border: 1px dashed #1c869a;
    border-radius: 6px;
    color: #1c869a;
    cursor: pointer;
    font-size: 12px;
    user-select: none;
    transition: background 0.15s;
}

.task-attach-btn:hover {
    background: #f0f9fb;
}

.task-attach-list {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4px;
}

.task-attach-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 4px 8px;
    border: 1px solid #ddd;
    border-radius: 4px;
    background: #fafafa;
    font-size: 12px;
}

.task-attach-info {
    display: flex;
    align-items: center;
    gap: 5px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
}

.task-attach-remove {
    background: none;
    border: none;
    color: #dc3545;
    cursor: pointer;
    font-size: 12px;
    padding: 0 4px;
    line-height: 1;
    flex-shrink: 0;
}

.task-attach-remove:hover {
    color: #a71d2a;
}

.composer-attachments>ul {
    list-style: none;
    padding: 0;
    margin: 6px 0 0 0;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
}

.composer-attachments>ul>li {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    background: #f8f9fa;
    border-radius: 4px;
    font-size: 12px;
    border: 1px solid #e9ecef;
    transition: background-color 0.2s;
}

.composer-attachments>ul>li:hover {
    background: #e9ecef;
}

.composer-attachments>ul>li>span {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 6px;
    flex-grow: 1;
    padding: 6px 8px;
    overflow: hidden;
}

.composer-attachments>ul>li>span>span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.composer-attachments>ul>li>span>i {
    color: #2e8b8b;
    font-size: 16px;
    flex-shrink: 0;
}

.attachment-size {
    color: #6c757d;
    font-size: 10px;
    flex-shrink: 0;
}

.download-attachment-btn {
    background: none;
    border: none;
    color: #2e8b8b;
    cursor: pointer;
    padding: 9px 10px;
    border-radius: 0 4px 4px 0 !important;
    text-decoration: none;
    transition: all 0.2s;
    font-size: 12px;
    flex-shrink: 0;
}

.download-attachment-btn:hover {
    background-color: #2e8b8b;
    color: white;
    text-decoration: none;
}

@media (max-width: 768px) {
    .composer-attachments>ul {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (max-width: 480px) {
    .composer-attachments>ul {
        grid-template-columns: 1fr;
    }
}
</style>
